<template>
  <div class="page-container">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>{{ $t('teacher.detailTitle') }}</span>
        </div>
      </template>

      <!-- Avatar Section -->
      <div v-if="teacherData.avatar" class="avatar-section" @click="previewAvatar(teacherData.avatar)">
        <ImagePreview 
          :src="teacherData.avatar" 
          width="120px" 
          height="120px"
          :enable-preview="false"
          fit="cover"
          round
        />
      </div>

      <el-descriptions :column="1" border>
        <el-descriptions-item :label="$t('teacher.name')">
          {{ teacherData.name }}
        </el-descriptions-item>
        <el-descriptions-item :label="$t('teacher.phone')">
          {{ teacherData.phone }}
        </el-descriptions-item>
        <el-descriptions-item :label="$t('teacher.status')">
          {{ getStatusText(teacherData.status) }}
        </el-descriptions-item>
        <el-descriptions-item :label="$t('teacher.age')">
          {{ teacherData.age || '-' }}
        </el-descriptions-item>
        <el-descriptions-item :label="$t('teacher.workExperience')">
          {{ teacherData.workExperienceYear || '-' }}
        </el-descriptions-item>
        <el-descriptions-item :label="$t('teacher.isVisibleForMiniApp')">
          <el-tag :type="teacherData.isVisibleForMiniApp ? 'success' : 'info'" size="small">
            {{ teacherData.isVisibleForMiniApp ? $t('teacher.showInMiniApp') : $t('teacher.hideFromMiniApp') }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item :label="$t('teacher.remark')">
          {{ teacherData.remark || '-' }}
        </el-descriptions-item>
        <el-descriptions-item :label="$t('common.createdAt')">
          {{ teacherData.createdAt }}
        </el-descriptions-item>
      </el-descriptions>

      <div class="mt-4">
        <el-button @click="goBack">
          {{ $t('common.back') }}
        </el-button>
        <el-button type="primary" @click="goToEdit">
          {{ $t('common.edit') }}
        </el-button>
      </div>
    </el-card>

    <!-- Image Preview Dialog -->
    <el-dialog v-model="showImagePreview" title="Avatar Preview" width="500px" center>
      <div style="text-align: center;">
        <img :src="previewImageUrl" alt="Avatar Preview" style="max-width: 100%; max-height: 80vh; border-radius: 8px;" />
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ElMessage } from 'element-plus'
import { getTeacherDetail, updateTeacher } from '@/api/teacher'

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const loading = ref(false)
const teacherData = ref({})
const showImagePreview = ref(false)
const previewImageUrl = ref('')

const getStatusText = (status) => {
  const statusMap = {
    0: t('teacher.inactive'),
    1: t('teacher.active'),
    2: t('teacher.deleted'),
    3: t('teacher.blocked')
  }
  return statusMap[status] || '-'
}

const loadTeacher = async () => {
  const id = route.params.id
  if (!id) return
  
  loading.value = true
  try {
    const response = await getTeacherDetail(id)
    teacherData.value = response.data
  } catch (error) {
    ElMessage.error('Failed to load teacher')
  } finally {
    loading.value = false
  }
}

const goBack = () => {
  router.push('/teacher')
}

const goToEdit = () => {
  router.push(`/teacher/edit/${route.params.id}`)
}

const previewAvatar = (avatarUrl) => {
  if (!avatarUrl) return
  previewImageUrl.value = avatarUrl
  showImagePreview.value = true
}

onMounted(() => {
  loadTeacher()
})
</script>

<style scoped>
.avatar-section {
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
}

.avatar-section :deep(.image-preview-wrapper),
.avatar-section :deep(.image-preview-container) {
  width: 120px !important;
  height: 120px !important;
  border-radius: 50%;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
}

.avatar-section :deep(.preview-img) {
  width: 100% !important;
  height: 100% !important;
  object-fit: cover !important;
}

.avatar-section :deep(.image-preview-container:hover) {
  transform: scale(1.05);
  box-shadow: 0 6px 16px rgba(64, 158, 255, 0.3);
}
</style>
